/* eslint-disable max-lines */
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import Input from '@components/atoms/ui-kit/input.tsx';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import defaultPrompt, {
  defaultSummarizationPrompt
} from '@utils/types/defaultPrompt.ts';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue
} from 'react-hook-form';
import { Dispatch, SetStateAction, useMemo, useRef } from 'react';
import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import { useAddExportBoTemplate } from '@utils/api/back-office/customer-bo/use-add-export-bo-template.tsx';
import { Params, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Template } from '@utils/types/organization/export-template.ts';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import { Upload } from 'lucide-react';

type Props = {
  newTemplates: Template[];
  reset: UseFormReset<Template>;
  errors: FieldErrors<Template>;
  control: Control<Template>;
  register: UseFormRegister<Template>;
  handleSubmit: UseFormHandleSubmit<Template, Template>;
  setCreateTemplateModal: Dispatch<SetStateAction<boolean>>;
  templateType: { value: string; label: string };
  createTemplateModal: boolean;
  setNewTemplates: Dispatch<SetStateAction<Template[]>>;
  setValue: UseFormSetValue<Template>;
  setTemplateType: Dispatch<
    SetStateAction<{
      value: string;
      label: string;
    }>
  >;
};

export default function CreateNewTemplateForm({
  newTemplates,
  errors,
  register,
  control,
  createTemplateModal,
  setCreateTemplateModal,
  handleSubmit,
  setTemplateType,
  templateType,
  setValue,
  setNewTemplates,
  reset
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { customerId } = useParams<Params>();
  const formRef = useRef<HTMLFormElement>(null);

  const { mutate: addTemplate, isLoading: addIsLoading } =
    useAddExportBoTemplate(Number(customerId));

  const getDefaultPrompt = (type: string) =>
    type === 'SUMMARIZATION' ? defaultSummarizationPrompt : defaultPrompt;

  const styles = useMemo(
    () =>
      StyleSheet.create({
        card: {
          backgroundColor: getColour('appSoft'),
          border: `1px solid ${getColour('hairline')}`,
          borderRadius: 16
        },
        cardHeader: {
          borderBottom: `1px solid ${getColour('hairline')}`
        },
        label: {
          color: getColour('normalText')
        },
        subtle: {
          color: getColour('icon')
        }
      }),
    [getColour]
  );

  const onSubmit = async () => {
    const savingToastId = toast(t('saving-templates'), { icon: '📝' });

    try {
      const templatePromises = newTemplates.map(template =>
        addTemplate(
          {
            name: template.name,
            template_docx:
              typeof template.template_docx === 'string'
                ? null
                : template.template_docx,
            template_example:
              typeof template.template_example === 'string'
                ? null
                : template.template_example,
            template_type: template.template_type,
            prompt: template.prompt
          },
          {
            onSuccess: () => {
              toast.success(t('export-template-updated-successfully'));
            },
            onError: (error: AxiosError<BaseError>) => {
              const errorMessage =
                error.response?.data?.error_description ||
                error?.response?.data.detail ||
                error?.response?.data.error ||
                error?.response?.data.message ||
                t('failed-to-update-the-export-template');
              toast.error(errorMessage);
            }
          }
        )
      );

      await Promise.all(templatePromises);
    } catch {
      toast.error(t('failed-to-add-some-templates'));
    } finally {
      toast.dismiss(savingToastId);
      reset();
      setCreateTemplateModal(false);
    }
  };

  const handleInputChange = <K extends keyof Template>(
    index: number,
    field: K,
    value: Template[K]
  ) => {
    setNewTemplates(prevTemplates => {
      const updatedTemplates = [...prevTemplates];
      const currentTemplate = updatedTemplates[index];

      if (field === 'template_type') {
        const newPrompt = getDefaultPrompt(value as string);
        updatedTemplates[index] = {
          ...currentTemplate,
          [field]: value,
          prompt: newPrompt
        };
        setValue('prompt', newPrompt);
      } else {
        updatedTemplates[index] = {
          ...currentTemplate,
          [field]:
            value ||
            getDefaultPrompt(currentTemplate.template_type || 'EXPORT_TEMPLATE')
        };
      }

      return updatedTemplates;
    });
  };

  const typeOptions = [
    { value: 'EXPORT_TEMPLATE', label: 'EXPORT_TEMPLATE' },
    { value: 'SUMMARIZATION', label: 'SUMMARIZATION' }
  ];

  const UploadBox = ({
    id,
    title,
    hint,
    tooltip,
    accept,
    file,
    error,
    onFile
  }: {
    id: string;
    title: string;
    hint: string;
    tooltip: string;
    accept: string;
    file: Template['template_docx'] | Template['template_example'];
    error?: string;
    onFile: (file: File) => void;
  }) => {
    const fileName =
      file && typeof file !== 'string' ? file.name : t('click-to-upload');

    return (
      <div className={cn(css(styles.card), 'overflow-hidden')}>
        <div
          className={cn(
            css(styles.cardHeader),
            'px-4 py-3 flex items-start justify-between gap-3'
          )}
        >
          <div className="min-w-0">
            <p className={cn(css(styles.label), 'text-sm font-semibold')}>
              {title}
            </p>
            <p className={cn(css(styles.subtle), 'text-xs mt-0.5')}>{hint}</p>
          </div>
          <span
            className={cn(
              css(styles.subtle),
              'text-[11px] px-2 py-1 rounded-full border whitespace-nowrap'
            )}
            style={{ borderColor: getColour('hairline') }}
          >
            {accept.includes('pdf') ? '.pdf' : '.docx'}
          </span>
        </div>

        <div className="p-4">
          <Tooltip label={tooltip}>
            <label
              htmlFor={id}
              className={cn(
                'group w-full rounded-xl border-2 border-dashed cursor-pointer transition-all',
                'px-4 py-4 flex items-center gap-3',
                'hover:opacity-90',
                'min-h-[72px]'
              )}
              style={{
                backgroundColor: getColour('thirdLayer'),
                borderColor: getColour('hairline')
              }}
            >
              <span
                className={cn(
                  'h-10 w-10 rounded-xl border grid place-items-center flex-shrink-0'
                )}
                style={{ borderColor: getColour('hairline') }}
              >
                <Upload width={18} className="opacity-70" />
              </span>

              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{fileName}</p>
                <p className="text-xs opacity-60 truncate">
                  {t('click-to-upload')}
                </p>
              </div>

              <span
                className={cn(
                  'text-xs font-medium px-3 py-2 rounded-2xl border whitespace-nowrap'
                )}
                style={{
                  borderColor: getColour('hairline'),
                  backgroundColor: getColour('appSoft')
                }}
              >
                {t('choose-file')}
              </span>

              <input
                id={id}
                type="file"
                accept={accept}
                className="sr-only"
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  onFile(f);
                }}
              />
            </label>
          </Tooltip>

          {!!error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={createTemplateModal}
      onClose={() => {
        setNewTemplates([
          {
            prompt: getDefaultPrompt(templateType.value),
            name: '',
            template_type: templateType.value,
            template_docx: null,
            template_example: null,
            id: 0,
            organization_id: ''
          }
        ]);
        setCreateTemplateModal(false);
      }}
      size="medium"
      title={t('create-template')}
      caption={t('create-the-templates-information-and-upload-the-new-files')}
      footerActions={[
        <Button
          onClick={() => setCreateTemplateModal(false)}
          label={t('cancel')}
          size={'small'}
          variant={'smoke'}
        />,
        <div className="flex justify-end">
          <Button
            label={t('save-template')}
            size={'small'}
            type={'submit'}
            key="create"
            onClick={() => formRef.current?.requestSubmit()}
            disabled={addIsLoading}
          />
        </div>
      ]}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-5"
        ref={formRef}
        key={'create'}
      >
        {newTemplates.map((template, index) => (
          <div key={template.id || `template-${index}`} className="space-y-5">
            {/* Top card: Prompt */}
            <div className={cn(css(styles.card), 'p-4 md:p-5')}>
              <Input
                id="prompt"
                label={t('prompt')}
                type="text"
                multiline={true}
                value={
                  template.prompt ||
                  getDefaultPrompt(template.template_type || 'EXPORT_TEMPLATE')
                }
                autoComplete="prompt"
                className="w-full text-md"
                {...register('prompt')}
                error={errors.prompt?.message}
                onChange={e =>
                  handleInputChange(index, 'prompt', e.target.value)
                }
              />
            </div>

            {/* Middle card: Name + Type (visually balanced row) */}
            <div className={cn(css(styles.card), 'p-4 md:p-5')}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                {/* Name */}
                <div className="md:col-span-8">
                  <Input
                    id="name"
                    label={t('name')}
                    type="text"
                    placeholder={t('template-name')}
                    autoComplete="name"
                    {...register('name')}
                    error={errors.name?.message}
                  />
                </div>

                {/* Type */}
                <div className="md:col-span-4">
                  <label
                    htmlFor="type"
                    className={cn(
                      css(styles.label),
                      'block text-sm font-medium mb-2'
                    )}
                  >
                    {t('type')}
                  </label>

                  {/* Give the select a consistent “field height” wrapper so it aligns with Input */}
                  <div className="h-10">
                    <Controller
                      control={control}
                      name="type"
                      defaultValue={templateType.value}
                      render={({ field: { onChange, value } }) => (
                        <SelectInput
                          id="type"
                          name="type"
                          value={
                            typeOptions.find(
                              option => option.value === value
                            ) ||
                            typeOptions.find(
                              option => option.value === templateType.value
                            )
                          }
                          onChange={selectedOption => {
                            const newValue = selectedOption
                              ? selectedOption.value
                              : 'EXPORT_TEMPLATE';
                            onChange(newValue);
                            setTemplateType(selectedOption);
                            handleInputChange(index, 'template_type', newValue);
                          }}
                          options={typeOptions}
                          isMulti={false}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom: uploads as two distinct cards with their own headers */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <UploadBox
                id={`template_docx_${index}`}
                title={t('template-docx')}
                hint={t('upload-template-docx')}
                tooltip={t('upload-template-docx')}
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                file={template.template_docx}
                error={errors.template_docx?.message as string | undefined}
                onFile={file => {
                  if (!file.name.toLowerCase().endsWith('.docx')) {
                    toast.error(t('please-upload-a-docx-file'));
                    return;
                  }
                  handleInputChange(index, 'template_docx', file);
                  setValue('template_docx', file);
                }}
              />

              <UploadBox
                id={`template_example_${index}`}
                title={t('template-pdf')}
                hint={t('upload-template-example')}
                tooltip={t('upload-template-example')}
                accept="application/pdf,.pdf"
                file={template.template_example}
                error={errors.template_example?.message as string | undefined}
                onFile={file => {
                  if (!file.name.toLowerCase().endsWith('.pdf')) {
                    toast.error(t('please-upload-a-pdf-file'));
                    return;
                  }
                  handleInputChange(index, 'template_example', file);
                  setValue('template_example', file);
                }}
              />
            </div>
          </div>
        ))}
      </form>
    </Modal>
  );
}
